/**
 * Data layer for the admin MF Overview: platform-wide mutual fund figures, and
 * every investor's MF records in one place.
 *
 *   GET mutual_fund/mf_investment_summary/?from_date=&to_date=   DD-MM-YYYY, both optional
 *   GET mutual_fund/{record}/list/?page=&page_size=&{record id}=  page_size defaults to 100
 *   GET mutual_fund/mf_plan_modification_instructions/get/{user_uuid}/
 *
 * Endpoints and parameters are exactly those in "Admin Penal APIs.txt". The
 * list endpoints are the ones the investor dashboard already calls with
 * `?user_id=`; without it they return every investor's records, in the same
 * shapes the user platform types (helpers/mfTransactions.ts,
 * mfRedemptionPlans.ts, mfSwitchPlans.ts, mfPlanModification.ts and
 * interfaces/IMutualFundDetails.ts there).
 *
 * Confirmed against live responses (24-09-2026): the summary (see
 * MF_SUMMARY_KEYS), and all seven lists —
 *
 *   { success, message, count, next, previous, data: [ ... ] }
 *
 * Six carry `user`, the investor's uuid, on every record. SIP modification
 * instructions do not; their investor is found through their SIP — see
 * fetchMFSipsById.
 */

import { makeGETRequestOnUserService } from "./api";
import type { MFRecord } from "./mfAdminInvestments";

export type MFListKind =
  | "purchases"
  | "sips"
  | "redemptions"
  | "switches"
  | "swps"
  | "stps"
  | "modifications";

export interface MFListConfig {
  kind: MFListKind;
  /** Tab label. */
  label: string;
  /** One record, in a sentence. */
  noun: string;
  path: string;
  /** The query parameter that narrows the list to one record, where the API takes one. */
  idParam?: string;
  /** The field a record carries its id in. */
  idField: string;
}

export const MF_LISTS: MFListConfig[] = [
  {
    kind: "purchases",
    label: "Lumpsum orders",
    noun: "Lumpsum order",
    path: "/mutual_fund/mf_purchase/list/",
    idParam: "purchase_id",
    idField: "purchase_id",
  },
  {
    kind: "sips",
    label: "SIPs",
    noun: "SIP",
    path: "/mutual_fund/mf_purchase_plans/list/",
    idParam: "purchase_plan_id",
    idField: "purchase_plan_id",
  },
  {
    kind: "redemptions",
    label: "Redemptions",
    noun: "Redemption",
    path: "/mutual_fund/mf_redemptions/list/",
    idParam: "redemption_id",
    idField: "redemption_id",
  },
  {
    kind: "switches",
    label: "Switches",
    noun: "Switch",
    path: "/mutual_fund/mf_switches/list/",
    idParam: "switch_id",
    idField: "switch_id",
  },
  {
    kind: "swps",
    label: "SWPs",
    noun: "Withdrawal plan (SWP)",
    path: "/mutual_fund/mf_redemption_plans/list/",
    idParam: "redemption_plan_id",
    idField: "redemption_plan_id",
  },
  {
    kind: "stps",
    label: "STPs",
    noun: "Transfer plan (STP)",
    path: "/mutual_fund/mf_switch_plans/list/",
    idParam: "switch_plan_id",
    idField: "switch_plan_id",
  },
  {
    kind: "modifications",
    label: "SIP modifications",
    noun: "SIP modification",
    path: "/mutual_fund/mf_plan_modification_instructions/list/",
    idField: "id",
  },
];

export const DEFAULT_PAGE_SIZE = 100;

export interface MFListPage {
  rows: MFRecord[];
  /** The total across every page, when the envelope says; otherwise null. */
  count: number | null;
}

const isRecord = (value: any): value is MFRecord =>
  !!value && typeof value === "object" && !Array.isArray(value);

const payloadOf = (response: any): any => response?.data ?? response;

/** The API can answer 200 with a failure envelope; treat that as a failure. */
const assertOk = (payload: any, fallback: string) => {
  if (
    isRecord(payload) &&
    (payload.success === false ||
      String(payload.status || "").trim().toLowerCase() === "failed")
  ) {
    throw new Error(String(payload.error || payload.message || fallback));
  }
  return payload;
};

const countFrom = (value: any): number | null => {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) return value;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) return Number(value);
  return null;
};

/** Rows and total, out of whichever envelope the list came back in. */
const readListPage = (payload: any): MFListPage => {
  const data = isRecord(payload) ? payload.data : undefined;
  const candidates = [
    payload,
    data,
    isRecord(payload) ? payload.results : undefined,
    isRecord(data) ? data.results : undefined,
    isRecord(data) ? data.data : undefined,
  ];
  const list = candidates.find((candidate) => Array.isArray(candidate)) || [];
  const counts = [
    isRecord(payload) ? payload.count : undefined,
    isRecord(data) ? data.count : undefined,
    isRecord(payload) ? payload.total : undefined,
  ];
  let count: number | null = null;
  counts.some((value) => {
    count = countFrom(value);
    return count !== null;
  });
  return { rows: (list as any[]).filter(isRecord), count };
};

export const fetchMFList = async (
  config: MFListConfig,
  options: { page: number; pageSize: number; id?: string }
): Promise<MFListPage> => {
  const params = new URLSearchParams();
  params.set("page", String(options.page));
  params.set("page_size", String(options.pageSize));
  const id = (options.id || "").trim();
  if (id && config.idParam) params.set(config.idParam, id);
  try {
    const response = await makeGETRequestOnUserService(
      `${config.path}?${params.toString()}`
    );
    return readListPage(
      assertOk(payloadOf(response), `${config.label} could not be loaded.`)
    );
  } catch (error) {
    // An empty list, or an id that matches nothing, reads as a 404 on these
    // endpoints — the investor dashboard treats it the same way.
    if ((error as any)?.response?.status === 404) return { rows: [], count: 0 };
    throw error;
  }
};

/** "2026-09-01" (a date input's value) → "01-09-2026", the format the API takes. */
const toApiDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split("-");
  return `${day}-${month}-${year}`;
};

/**
 * The six activity types the summary totals, in its own keys. Confirmed
 * against a live response (24-09-2026):
 *
 *   { success, message, data: {
 *       purchase, purchase_plan, redemption, redemption_plan, switch,
 *       switch_plan, total: { total_count, total_amount } } }
 *
 * `total` is the sum of the six, counts and amounts alike.
 */
export const MF_SUMMARY_KEYS = [
  "purchase",
  "purchase_plan",
  "redemption",
  "redemption_plan",
  "switch",
  "switch_plan",
] as const;

export type MFSummaryKey = typeof MF_SUMMARY_KEYS[number];

/** One activity type's totals; null where the response left a figure out. */
export interface MFSummaryBucket {
  count: number | null;
  amount: number | null;
}

export interface MFInvestmentSummary {
  buckets: Record<MFSummaryKey, MFSummaryBucket>;
  total: MFSummaryBucket;
}

const numberFrom = (value: any): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

const bucketFrom = (value: any): MFSummaryBucket => ({
  count: isRecord(value) ? numberFrom(value.total_count) : null,
  amount: isRecord(value) ? numberFrom(value.total_amount) : null,
});

/** The summary for a date range, or for all time when neither end is set. */
export const fetchMFInvestmentSummary = async (range: {
  from?: string;
  to?: string;
}): Promise<MFInvestmentSummary> => {
  const params = new URLSearchParams();
  if (range.from) params.set("from_date", toApiDate(range.from));
  if (range.to) params.set("to_date", toApiDate(range.to));
  const query = params.toString();
  const payload = assertOk(
    payloadOf(
      await makeGETRequestOnUserService(
        `/mutual_fund/mf_investment_summary/${query ? `?${query}` : ""}`
      )
    ),
    "The investment summary could not be loaded."
  );
  const data = isRecord(payload) && isRecord(payload.data) ? payload.data : payload;
  const buckets = {} as Record<MFSummaryKey, MFSummaryBucket>;
  MF_SUMMARY_KEYS.forEach((key) => {
    buckets[key] = bucketFrom(isRecord(data) ? data[key] : undefined);
  });
  return { buckets, total: bucketFrom(isRecord(data) ? data.total : undefined) };
};

/**
 * The investor a record belongs to, or "" when it names none.
 *
 * The user platform's record types call it `user`. The other spellings are
 * there until a live list response confirms which one these endpoints use.
 */
export const investorUuidOf = (row: MFRecord): string => {
  const candidates = [
    row.user,
    row.user_uuid,
    row.user_id,
    isRecord(row.user_details) ? row.user_details.uuid : undefined,
  ];
  for (const candidate of candidates) {
    const value = isRecord(candidate) ? candidate.uuid : candidate;
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
};

/*
 * SIP modification instructions name their SIP (`plan`) but not its investor
 * (live list, 24-09-2026: no `user` on the record). The SIP does name its
 * investor, and the SIP list takes a `purchase_plan_id` filter — so the owner
 * is found exactly, never guessed from the consent contact details (a family's
 * accounts can share a mobile). A SIP's owner never changes, so each is looked
 * up once per visit.
 */
const sipCache: Record<string, MFRecord | null> = {};

/** SIP records by purchase plan id, for the ids given; unknown ids are left out. */
export const fetchMFSipsById = async (planIds: string[]): Promise<Record<string, MFRecord>> => {
  const sipList = MF_LISTS.filter((item) => item.kind === "sips")[0];
  const wanted = planIds.filter((id, index) => id && planIds.indexOf(id) === index);
  const missing = wanted.filter((id) => !(id in sipCache));
  let cursor = 0;
  // A few at a time: a page of instructions can name dozens of SIPs.
  const worker = async () => {
    while (cursor < missing.length) {
      const id = missing[cursor];
      cursor += 1;
      try {
        const page = await fetchMFList(sipList, { page: 1, pageSize: 1, id });
        // Only an exact match counts. If the filter were ignored, the first SIP
        // returned would be someone else's — and would leak this record to
        // that investor's admin.
        sipCache[id] = page.rows.filter((row) => row.purchase_plan_id === id)[0] || null;
      } catch (error) {
        // Left uncached, so the next load tries again.
      }
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);
  const found: Record<string, MFRecord> = {};
  wanted.forEach((id) => {
    const sip = sipCache[id];
    if (sip) found[id] = sip;
  });
  return found;
};

/** A record's own id, for keys, the details view and the id filter. */
export const recordIdOf = (config: MFListConfig, row: MFRecord): string =>
  String(row[config.idField] || row.id || row.uuid || "").trim();
